import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../Modal.mjs';
import ClosingX from '../ClosingX';
import EmptyState from '../common/EmptyState';
import InlineBanner from '../common/InlineBanner';
import ProgressBar from '../common/ProgressBar';
import MatchCard from './MatchCard';
import { searchByFile } from '../../services/ReverseSearchService.mjs';
import { describeIndexState } from '../../utils/reverseSearchFormat.js';
import '../../css/SimilarAssets.css';
import '../../css/ReverseSearch.css';

/**
 * Reverse search: drop a file from your machine, get the assets that match it.
 *
 * ── Results are GROUPED BY BAND, not sorted by score ────────────────────────
 * The bands answer genuinely different questions — "this IS your file", "your
 * file is a piece of this", "this looks like it" — and a flat list sorted by a
 * hidden number makes them look like degrees of one thing. Grouping puts the
 * question in a heading, so a user scanning for "did I already upload this?"
 * can stop reading after the first group.
 *
 * ── The upload stage is shown separately from the search stage ──────────────
 * Sending 40 MB and then searching are seconds apart in the best case and
 * minutes apart on a bad connection. One spinner covering both reads as a hang;
 * a progress bar that becomes a spinner reads as progress.
 */

/** Bands in the order they are presented — strongest claim first. */
const BAND_ORDER = ['exact', 'near-identical', 'contains', 'contained-in', 'similar', 'related'];

/**
 * Headings are per-modality: "The same image" is wrong for a WAV, and a
 * heading that does not match what the user dropped undermines the result
 * underneath it.
 */
const BAND_HEADINGS = {
    'exact':          'Already in your library',
    'near-identical': { image: 'The same image', audio: 'The same recording', default: 'The same file' },
    'contains':       { audio: 'Your clip appears in these', default: 'Your file appears in these' },
    'contained-in':   { audio: 'These appear in your clip', default: 'These appear in your file' },
    'similar':        'Similar assets',
    'related':        'Loosely related',
};

function headingFor(band, modality) {
    const heading = BAND_HEADINGS[band];
    if (typeof heading === 'string') return heading;
    return heading?.[modality] ?? heading?.default ?? band;
}

const STAGE_LABELS = {
    preparing: 'Preparing…',
    uploading: 'Uploading…',
    searching: 'Searching your library…',
};

const ReverseSearchModal = ({ file, onClose, onOpenFile }) => {
    const [stage, setStage] = useState('preparing');
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    // A preview of what the user dropped, so the results have something to be
    // "matches for". Object URLs must be revoked or every search leaks a blob.
    const [previewUrl, setPreviewUrl] = useState(null);
    useEffect(() => {
        if (!file || !file.type?.startsWith('image/')) return undefined;
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const abortRef = useRef(null);

    const run = useCallback(async () => {
        setError(null);
        setResult(null);
        setStage('preparing');

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const data = await searchByFile(file, {
                signal: controller.signal,
                onStage: (next, fraction) => {
                    setStage(next);
                    if (typeof fraction === 'number') setProgress(fraction);
                },
            });
            if (controller.signal.aborted) return;
            setResult(data);
            setStage('done');
        } catch (err) {
            if (controller.signal.aborted) return;
            setError(err?.response?.data?.error || 'The search could not be completed.');
            setStage('done');
        }
    }, [file]);

    useEffect(() => {
        run();
        return () => abortRef.current?.abort();
    }, [run]);

    const grouped = BAND_ORDER
        .map((band) => ({ band, hits: (result?.results || []).filter((hit) => hit.band === band) }))
        .filter((group) => group.hits.length > 0);

    const indexNote = describeIndexState(result?.indexState);
    const busy = stage !== 'done';

    return (
        <Modal onClose={onClose} size="lg" aria-label="Find matching assets">
            <div className="rs-body">
                <div className="rs-head">
                    <h2 className="rs-title">Find matching assets</h2>
                    <ClosingX onClick={onClose} />
                </div>

                <header className="rs-query">
                    <div className="rs-query-thumb">
                        {previewUrl
                            ? <img src={previewUrl} alt="" />
                            : <span className="rs-query-ext">{(file.name.split('.').pop() || '?').toUpperCase()}</span>}
                    </div>
                    <div className="rs-query-meta">
                        <p className="rs-query-name">{file.name}</p>
                        <p className="rs-query-note">
                            Searching by content. This file is not added to your library.
                        </p>
                    </div>
                </header>

                {busy && (
                    <div className="rs-progress" role="status">
                        <p className="rs-stage">{STAGE_LABELS[stage] || 'Working…'}</p>
                        {stage === 'uploading'
                            ? <ProgressBar value={Math.round(progress * 100)} max={100} showPercent />
                            : <ProgressBar />}
                    </div>
                )}

                {!busy && error && (
                    <div className="sa-error">
                        <InlineBanner variant="danger">{error}</InlineBanner>
                        <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={run}>
                            Try again
                        </button>
                    </div>
                )}

                {!busy && !error && indexNote && <InlineBanner variant="info">{indexNote}</InlineBanner>}

                {!busy && !error && grouped.length === 0 && (
                    <EmptyState
                        title="No matches"
                        description={
                            "Nothing in this workspace matches that file. If your library was uploaded before "
                            + "content search was enabled, it may still need a one-off backfill."
                        }
                    />
                )}

                {!busy && !error && grouped.map(({ band, hits }) => (
                    <section key={band} className="rs-group">
                        <h3 className="rs-group-title">
                            {headingFor(band, result?.modality)}
                            <span className="rs-group-count">{hits.length}</span>
                        </h3>
                        <div className="sa-grid">
                            {hits.map((hit) => (
                                <MatchCard key={hit.fileId} hit={hit} onOpen={onOpenFile} showBand={false} />
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </Modal>
    );
};

export default ReverseSearchModal;
